import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Divider, Heading, Modal, ProgressStepper } from '@zillow/constellation-10';

import ExitPaymentSetup from '../ExitPaymentSetup/component';
import WizardShellToasts from './WizardShellToasts';
import { WizardShellContext } from './context';
import { getWizardScreen, WIZARD_CATEGORIES } from './screens';
import type { WizardCategory } from './screens';

type Props = {
  /** Current route pathname; drives the stepper and per-step focus/scroll reset. */
  activePathname: string;
  /** Categories the current flow skips entirely (e.g. Lease when the property has no leases). */
  hiddenCategories?: Array<WizardCategory>;
  /** Called after the user confirms in the exit dialog. Omit to hide the Exit button. */
  onSaveAndExit?: () => void;
  children: ReactNode;
};

const WizardShell = ({ activePathname, hiddenCategories, onSaveAndExit, children }: Props): JSX.Element => {
  const [footer, setFooter] = useState<ReactNode>(null);
  const [aside, setAside] = useState<ReactNode>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const contextValue = useMemo(() => ({ setFooter, setAside }), []);
  const currentScreen = getWizardScreen(activePathname);

  const visibleCategories = WIZARD_CATEGORIES.filter((category) => !hiddenCategories?.includes(category));
  const activeStepIndex = currentScreen?.category ? visibleCategories.indexOf(currentScreen.category) : 0;
  const showStepper = Boolean(currentScreen?.showStepper) && activeStepIndex !== -1;

  const steps = visibleCategories.map((label, index) => ({
    label,
    tone:
      currentScreen?.category && activeStepIndex > index
        ? ('complete' as const)
        : currentScreen?.category && activeStepIndex === index
          ? ('incomplete' as const)
          : undefined,
  }));

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      bodyRef.current.focus({ preventScroll: true });
    }
  }, [activePathname]);

  return (
    <WizardShellContext.Provider value={contextValue}>
      <Modal.Root
        size="fullScreen"
        open
        onOpenChange={() => {
          /* The wizard never dismisses itself; exiting goes through the Exit confirm. */
        }}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header
                css={{
                  display: 'flex',
                  paddingInlineStart: { base: 'layout.tight', lg: 'layout.loosest' },
                  paddingInlineEnd: { base: 'layout.tight', lg: 'layout.loosest' },
                  alignItems: 'center',
                  gap: 'layout.tight',
                  justifyContent: 'space-between',
                }}
              >
                <Heading level={1} textStyle="heading-xs">
                  Payment setup
                </Heading>
                {onSaveAndExit && (
                  <Button tone="neutral" emphasis="outlined" onClick={() => setShowExitConfirm(true)}>
                    Exit
                  </Button>
                )}
              </Modal.Header>
              <Divider />
              <Modal.Body>
                <WizardShellToasts />
                {showStepper && (
                  <ProgressStepper
                    css={{ marginBottom: 'layout.loose', marginTop: { base: 'layout.loose', mdDown: 'none' } }}
                    activeStepIndex={activeStepIndex}
                    steps={steps}
                  />
                )}
                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', xxl: 'repeat(24, 1fr)' },
                    columnGap: 'layout.tight',
                    rowGap: 'layout.default',
                    paddingBottom: 'layout.tight',
                  }}
                >
                  <Box css={{ display: { base: 'none', xxl: 'block' }, gridColumnEnd: { xxl: 'span 3' } }} />
                  <Box ref={bodyRef} tabIndex={-1} css={{ gridColumnEnd: { xxl: 'span 12' } }}>
                    {children}
                  </Box>
                  {aside && <Box css={{ gridColumnEnd: { xxl: 'span 6' } }}>{aside}</Box>}
                  <Box css={{ display: { base: 'none', xxl: 'block' }, gridColumnEnd: { xxl: 'span 3' } }} />
                </Box>
              </Modal.Body>
              {footer && (
                <>
                  <Divider />
                  <Modal.Footer
                    css={{
                      paddingInlineStart: { base: 'layout.tight', lg: 'layout.loosest' },
                      paddingInlineEnd: { base: 'layout.tight', lg: 'layout.loosest' },
                    }}
                  >
                    {footer}
                  </Modal.Footer>
                </>
              )}
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      {showExitConfirm && onSaveAndExit && (
        <ExitPaymentSetup
          exitPaymentSetup={() => {
            setShowExitConfirm(false);
            onSaveAndExit();
          }}
          onBack={() => setShowExitConfirm(false)}
        />
      )}
    </WizardShellContext.Provider>
  );
};

export default WizardShell;
